import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, NavigationType, NavLink, Outlet, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Icon } from './ui';
import { useMe } from './hooks';
import { api, type Changelog } from './lib/api';
import { FeedbackButton } from './feedback';
import { BarTitleContext, type BarTitles } from './barTitle';

const SITE = 'https://www.tri-team.de/triteam2022/';
const SITE_NAV: [string, string][] = [
  ['Abteilung', 'abteilung/'],
  ['Training', 'training/'],
  ['Veranstaltungen', 'veranstaltungen/'],
  ['Vorträge', 'votragsreihe/'],
  ['Berichte', 'berichte/'],
  ['Fotos', 'fotos/'],
  ['Sportplaner', 'sportplaner/'],
  ['Kontakt', 'kontakt/'],
];
/** The round "+" on phones and tablets (the page heads hide their add button there): target, label, admins only */
const FAB: Record<string, [string, string, boolean?]> = {
  '/': ['/termine/neu', 'Neuen Termin anlegen'],
  '/kalender': ['/termine/neu', 'Neuen Termin anlegen'],
  '/flohmarkt': ['/flohmarkt/neu', 'Anzeige aufgeben'],
  '/mitteilungen': ['/mitteilungen/neu', 'Mitteilung schreiben'],
  '/mitglieder': ['/mitglieder/neu', 'Mitglied hinzufügen', true],
};

const TABS: [string, string, string][] = [
  ['/', 'Termine', 'list'],
  ['/kalender', 'Kalender', 'cal'],
  ['/flohmarkt', 'Flohmarkt', 'tag'],
  ['/mitglieder', 'Mitglieder', 'users'],
  ['/mitteilungen', 'Mitteilungen', 'mega'],
];

/** Scroll position per page visit: back (button, gesture, close button) returns to where you were, new pages start at the top. */
const scrollPositions = new Map<string, number>();
function useScrollMemory() {
  const loc = useLocation();
  const type = useNavigationType();
  const current = useRef(loc.key);
  const lastPath = useRef(loc.pathname);
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    const save = () => scrollPositions.set(current.current, window.scrollY);
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, []);
  useLayoutEffect(() => {
    current.current = loc.key;
    if (loc.hash) return; // links with #anchor scroll there themselves
    if (type === NavigationType.Replace) return; // same page, other filter/day: keep the position
    const y = type === NavigationType.Pop ? (scrollPositions.get(loc.key) ?? 0) : 0;
    if (!y) {
      if (type === NavigationType.Push && lastPath.current === loc.pathname) return; // only the query changed
      lastPath.current = loc.pathname;
      window.scrollTo(0, 0);
      return;
    }
    lastPath.current = loc.pathname;
    // the list may still be building up: try until the page is tall enough (at most ~1.5 s)
    let tries = 0;
    let timer = 0;
    const restore = () => {
      if (document.documentElement.scrollHeight - window.innerHeight >= y || ++tries > 30) window.scrollTo(0, y);
      else timer = window.setTimeout(restore, 50);
    };
    restore();
    return () => window.clearTimeout(timer);
  }, [loc.key, loc.hash, loc.pathname, type]);
}

export default function Shell() {
  useScrollMemory();
  const { data: me } = useMe();
  const nav = useNavigate();
  const loc = useLocation();
  const fab = Object.hasOwn(FAB, loc.pathname) ? FAB[loc.pathname] : undefined;
  // phones: the page title in the app bar (the last page/form that set one wins)
  const [titles, setTitles] = useState<[symbol, string][]>([]);
  const barTitles = useMemo<BarTitles>(
    () => ({
      add: (id, title) => setTitles(list => [...list, [id, title]]),
      remove: id => setTitles(list => list.filter(([i]) => i !== id)),
    }),
    [],
  );
  const barTitle = titles.at(-1)?.[1] ?? null;
  // Links with #anchor (e.g. /profil#feedback, /neu#v0.2.0): scroll there once the content has loaded
  useEffect(() => {
    if (!loc.hash || loc.pathname.startsWith('/login')) return;
    const id = decodeURIComponent(loc.hash.slice(1));
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 30) {
        clearInterval(timer);
        el?.scrollIntoView({ block: 'start' });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [loc.pathname, loc.hash]);
  const changelog = useQuery({
    queryKey: ['changelog'],
    queryFn: () => api<Changelog>('/changelog'),
    enabled: !!me,
    staleTime: 10 * 60_000,
  });
  return (
    <>
      <header className="site">
        <div className="wrap site-in">
          <a className="logo-fallback" href={SITE} aria-label="Tri-Team SSV Ettlingen – zur Vereinsseite">
            <Icon name="swim" />
            <Icon name="bike" />
            <Icon name="run" />
            <span>
              Tri-Team{' '}
              <small
                style={{ font: '500 12px var(--f-body)', letterSpacing: '1.6px', color: 'var(--muted)', marginLeft: 6 }}
              >
                SSV Ettlingen
              </small>
            </span>
          </a>
          <nav className="site-nav" aria-label="Vereinsseite">
            {SITE_NAV.map(([l, p]) => (
              <a key={p} href={SITE + p} aria-current={l === 'Sportplaner' ? 'page' : undefined}>
                {l}
              </a>
            ))}
          </nav>
          <a className="site-back" href={SITE}>
            tri-team.de ↗
          </a>
        </div>
      </header>
      <div className="appbar">
        <div className="wrap appbar-in">
          <NavLink className={barTitle ? 'appname has-title' : 'appname'} to="/">
            Sportplaner
          </NavLink>
          {/* phones: the page title instead of "Sportplaner" (screen readers get the heading in the page) */}
          {barTitle && (
            <span className="bar-title" aria-hidden="true">
              {barTitle}
            </span>
          )}
          <nav className="tabs" aria-label="Sportplaner">
            {TABS.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === '/'}>
                {label}
              </NavLink>
            ))}
            {me?.role === 'admin' && <NavLink to="/admin">Admin</NavLink>}
          </nav>
          <div className="bar-actions">
            {me && <FeedbackButton />}
            {me && (
              <button
                className="me-btn"
                onClick={() => void nav('/profil')}
                aria-label={changelog.data?.unseen ? 'Mein Profil – es gibt Neuigkeiten' : 'Mein Profil'}
              >
                <Avatar person={{ id: me.id, name: `${me.first_name} ${me.last_name}`, photo: me.photo }} me />
                {changelog.data?.unseen && <i className="new-dot" aria-hidden="true" />}
              </button>
            )}
            {me && (
              <button className="newbtn" onClick={() => void nav('/termine/neu')}>
                <Icon name="plus" sw={2} />
                Termin
              </button>
            )}
          </div>
        </div>
      </div>
      <main className="wrap" id="main">
        <BarTitleContext.Provider value={barTitles}>
          <Outlet />
        </BarTitleContext.Provider>
      </main>
      <footer className="club-foot">
        <div className="wrap">
          <div>
            <h4>Tri-Team SSV Ettlingen</h4>
            <p>Abteilung Triathlon · Swim · Bike · Run</p>
          </div>
          <div>
            <h4>Sportplaner</h4>
            <p>Termine, Zusagen und Austausch für Mitglieder.</p>
          </div>
          <div>
            <h4>Mehr</h4>
            <p>
              <Link to="/hilfe">Hilfe: App installieren, Push, Anmelden</Link>
            </p>
            <p>
              <a href={SITE}>tri-team.de</a>
            </p>
            {me && (
              <p>
                <Link to="/neu">Was ist neu</Link>
                {changelog.data ? ` · Version ${changelog.data.version}` : ''}
                {me.role === 'admin' && (
                  <>
                    {' · '}
                    <Link to="/admin">Admin-Bereich</Link>
                  </>
                )}
              </p>
            )}
          </div>
        </div>
      </footer>
      <nav className="tabbar" aria-label="Sportplaner">
        {TABS.map(([to, label, icon]) => (
          <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'active' : undefined)}>
            <Icon name={icon} sw={1.8} />
            {label === 'Mitglieder' ? 'Leute' : label === 'Mitteilungen' ? 'Infos' : label}
          </NavLink>
        ))}
      </nav>
      {/* only on the lists: on forms and detail pages it would cover text and inputs */}
      {me && fab && (!fab[2] || me.role === 'admin') && (
        <button className="fab" onClick={() => void nav(fab[0])} aria-label={fab[1]}>
          <Icon name="plus" sw={2} />
        </button>
      )}
    </>
  );
}
